import { useEffect, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Download, Eye, FileSpreadsheet } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { EFFECTIVE_STATUS, EFFECTIVE_STATUS_CLS } from "@/constants/roles";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import AttendanceDetailDialog from "@/components/AttendanceDetailDialog";

function fmt(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
}

export default function AdminReports() {
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [unitId, setUnitId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [spkFilter, setSpkFilter] = useState("");
  const [units, setUnits] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const openDetail = async (row) => {
    try {
      const { data } = await api.get(`/admin/attendance/${row.attendance_id}`);
      setDetail(data);
    } catch (_e) { setDetail(row); }
  };

  useEffect(() => { (async () => { const { data } = await api.get("/work-units"); setUnits(data); })(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.date_from = format(from, "yyyy-MM-dd");
      if (to) params.date_to = format(to, "yyyy-MM-dd");
      if (unitId) params.unit_id = unitId;
      if (statusFilter) params.status = statusFilter;
      if (spkFilter.trim()) params.spk = spkFilter.trim();
      const { data } = await api.get("/admin/attendance", { params });
      setRows(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    // debounce SPK search
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [from, to, unitId, statusFilter, spkFilter]);

  const buildParams = () => {
    const params = new URLSearchParams();
    if (from) params.set("date_from", format(from, "yyyy-MM-dd"));
    if (to) params.set("date_to", format(to, "yyyy-MM-dd"));
    if (unitId) params.set("unit_id", unitId);
    if (statusFilter) params.set("status", statusFilter);
    if (spkFilter.trim()) params.set("spk", spkFilter.trim());
    return params;
  };
  const exportCsv = () => {
    window.open(`${API_BASE}/admin/reports/export?${buildParams().toString()}`, "_blank");
  };
  const exportMatrix = () => {
    if (!from || !to) return alert("Pilih rentang tanggal lebih dulu untuk Ekspor Rekap Excel.");
    window.open(`${API_BASE}/admin/reports/export-matrix?${buildParams().toString()}`, "_blank");
  };

  return (
    <div data-testid={TESTIDS.adminReportsPage} className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-tiny">Laporan</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">
            Laporan Kehadiran
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid={TESTIDS.exportCsvBtn} onClick={exportCsv}
            className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 hover:bg-klein transition-colors text-sm font-semibold">
            <Download className="w-4 h-4" /> Ekspor CSV
          </button>
          <button data-testid="export-matrix-btn" onClick={exportMatrix}
            className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 hover:bg-black transition-colors text-sm font-semibold">
            <FileSpreadsheet className="w-4 h-4" /> Rekap Excel (Matrix)
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <DatePick testid={TESTIDS.dateFromPicker} label="Dari Tanggal" value={from} onChange={setFrom} />
        <DatePick testid={TESTIDS.dateToPicker} label="Sampai Tanggal" value={to} onChange={setTo} />
        <div>
          <div className="label-tiny mb-2">Unit Kerja</div>
          <select data-testid="report-unit-filter" value={unitId} onChange={(e) => setUnitId(e.target.value)}
            className="w-full border border-black/20 px-3 py-2 bg-white text-sm rounded-none">
            <option value="">Semua Unit Kerja</option>
            {units.map((u) => <option key={u.unit_id} value={u.unit_id}>{u.code ? `[${u.code}] ` : ""}{u.name}</option>)}
          </select>
        </div>
        <div>
          <div className="label-tiny mb-2">Status Absensi</div>
          <select data-testid="report-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full border border-black/20 px-3 py-2 bg-white text-sm rounded-none">
            <option value="">Semua Status</option>
            {Object.entries(EFFECTIVE_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <div className="label-tiny mb-2">SPK</div>
          <input data-testid="report-spk-filter" value={spkFilter} onChange={(e) => setSpkFilter(e.target.value)}
            placeholder="Cari SPK..." className="w-full border border-black/20 px-3 py-2 bg-white text-sm rounded-none" />
        </div>
      </div>

      <div className="border border-black/10 bg-white overflow-x-auto">
        <table data-testid={TESTIDS.reportsTable} className="w-full text-sm min-w-[900px]">
          <thead className="bg-black text-white text-left">
            <tr>
              <Th>Tanggal</Th>
              <Th>NPP</Th>
              <Th>Nama</Th>
              <Th>Unit Kerja</Th>
              <Th>Status Absensi</Th>
              <Th>Check-in</Th>
              <Th>Check-out</Th>
              <Th>Jarak</Th>
              <Th>Aksi</Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="p-8 text-center text-gray-500">Memuat...</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-gray-500">Tidak ada data.</td></tr>
            )}
            {rows.map((r) => {
              const es = r.effective_status || (r.status === "late" ? "terlambat" : "ontime");
              return (
                <tr key={r.attendance_id} className="border-t border-black/10 hover:bg-gray-50">
                  <Td className="font-mono">{r.date}</Td>
                  <Td className="font-mono text-xs">{r.user_npp || <span className="text-gray-400">—</span>}</Td>
                  <Td className="font-semibold">{r.user_name}</Td>
                  <Td className="text-xs">{r.user_unit_name || <span className="text-gray-400">—</span>}</Td>
                  <Td>
                    <span className={`px-2 py-1 text-xs font-bold uppercase tracking-widest ${EFFECTIVE_STATUS_CLS[es] || "bg-gray-200"}`}>
                      {EFFECTIVE_STATUS[es] || "—"}
                    </span>
                  </Td>
                  <Td className="font-mono">{fmt(r.check_in_time)}</Td>
                  <Td className="font-mono">{fmt(r.check_out_time)}</Td>
                  <Td className="font-mono">{r.check_in_distance ?? "—"} m</Td>
                  <Td>
                    <button
                      data-testid={`view-detail-${r.attendance_id}`}
                      onClick={() => openDetail(r)}
                      className="inline-flex items-center gap-1 border border-black/20 hover:bg-black hover:text-white px-2 py-1 text-xs font-semibold transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" /> Detail
                    </button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AttendanceDetailDialog record={detail} open={!!detail} onClose={() => setDetail(null)} />
    </div>
  );
}

function DatePick({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="label-tiny mb-2">{label}</div>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            data-testid={testid}
            variant="outline"
            className="w-full justify-start border-black/20 rounded-none bg-white hover:bg-gray-50"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? format(value, "dd MMMM yyyy") : "Pilih tanggal"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 rounded-none" align="start">
          <Calendar mode="single" selected={value} onSelect={onChange} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
